import React from 'react';
import {
    Callout, DefaultButton, DirectionalHint, Dropdown, Icon, IconButton, IContextualMenuProps,
    IDropdownOption, Label, Stack, Text, TextField, Toggle, TooltipHost,
} from '@fluentui/react';
import { ILayoutElement, LayoutElementType } from '../layout';
import { IHeaderLink } from '../headerLinksConfig';
import { LayoutDndContext, dropAfter, isLayoutDrag } from './layoutDnd';
import { DynamicSetting } from '../../../Common/Setting';

// AC-950 / S3.2 — the element picker + per-element config for one column of the layout editor.
//
// Renders a column's elements as a compact, reorderable list; an "Add element" menu inserts a typed
// element; a per-element gear opens a Callout with the config fields that element actually reads at
// runtime (see ac-storefront-web LayoutRenderer's registry). Only the shipping element types are
// offered — types that don't render yet (menu / social / contact / html) are intentionally omitted so
// the editor can never produce a cell that shows nothing on the storefront.

interface IProps {
    elements: ILayoutElement[];
    onChange: (elements: ILayoutElement[]) => void;
    // AC-964 / S6.2 — this cell's coordinates enable element drag-and-drop across columns (via the
    // shared LayoutDndContext the editor provides). Absent = buttons-only (no drag).
    location?: { row: number; col: number };
}

// The element palette. Order = the order shown in the "Add element" menu (and the S6.3 drag palette).
export const PALETTE: { type: LayoutElementType; label: string; icon: string }[] = [
    { type: 'logo', label: 'Logo', icon: 'Photo2' },
    { type: 'title', label: 'Store title', icon: 'Font' },
    { type: 'text', label: 'Text', icon: 'TextField' },
    { type: 'link', label: 'Link', icon: 'Link' },
    { type: 'linkGroup', label: 'Link group', icon: 'BulletedList2' },
    { type: 'menu', label: 'Menu (dropdown)', icon: 'ContextMenu' },
    { type: 'search', label: 'Search bar', icon: 'Search' },
    { type: 'cart', label: 'Cart', icon: 'ShoppingCart' },
    { type: 'account', label: 'Account', icon: 'Contact' },
    { type: 'social', label: 'Social icons', icon: 'Share' },
    { type: 'contact', label: 'Contact info', icon: 'Mail' },
    { type: 'image', label: 'Image', icon: 'FileImage' },
    { type: 'html', label: 'Custom HTML', icon: 'Code' },
    { type: 'divider', label: 'Divider', icon: 'Separator' },
    { type: 'spacer', label: 'Spacer (flexible gap)', icon: 'Move' },
];
const META = (t: LayoutElementType) => PALETTE.find((p) => p.type === t);

// Types with no configurable props — they render purely from settings or as fixed spacers/slots.
// `social` / `contact` draw from the existing footer* settings fields; `social` has a size knob.
const NO_CONFIG: ReadonlySet<LayoutElementType> = new Set<LayoutElementType>(['logo', 'title', 'search', 'cart', 'account', 'contact', 'spacer']);

let seq = 0;
const genId = (type: string): string => `${type}-${(seq++).toString(36)}-${Math.floor(Math.random() * 1e6).toString(36)}`;

const LayoutElementCell: React.FC<IProps> = ({ elements, onChange, location }) => {
    const [editing, setEditing] = React.useState<string | null>(null);
    const dnd = React.useContext(LayoutDndContext);
    const [cellHot, setCellHot] = React.useState(false);
    const dndActive = !!(dnd && location);
    const draggingElement = dndActive && dnd?.drag?.kind === 'element';
    const draggingNewElement = dndActive && dnd?.drag?.kind === 'new-element';
    const draggingAny = draggingElement || draggingNewElement;
    const row = location?.row ?? -1;
    const col = location?.col ?? -1;
    const dimmed = (idx: number) => !!(draggingElement && dnd?.drag?.rowIndex === row && dnd?.drag?.colIndex === col && dnd?.drag?.elIndex === idx);
    const indicatorAt = (idx: number) => {
        const o = dnd?.over;
        return dndActive && o && o.row === row && o.col === col && o.index === idx ? o.after : null;
    };
    // Route a drop to a move (existing element) or an add (palette element), by drag kind.
    const performDrop = (insertAt: number) => {
        if (draggingElement) dnd?.dropElement({ row, col, insertAt });
        else if (draggingNewElement && dnd?.drag?.elementType) dnd.addElementAt({ row, col, insertAt }, dnd.drag.elementType);
    };

    const setEl = (idx: number, patch: Partial<ILayoutElement>) => onChange(elements.map((e, i) => (i === idx ? { ...e, ...patch } : e)));
    const addEl = (type: LayoutElementType) => onChange([...elements, { id: genId(type), type, props: {} }]);
    const delEl = (idx: number) => onChange(elements.filter((_, i) => i !== idx));
    const moveEl = (idx: number, dir: -1 | 1) => {
        const j = idx + dir;
        if (j < 0 || j >= elements.length) return;
        const next = [...elements];
        [next[idx], next[j]] = [next[j], next[idx]];
        onChange(next);
    };

    const addMenu: IContextualMenuProps = {
        items: PALETTE.map((p) => ({ key: p.type, text: p.label, iconProps: { iconName: p.icon }, onClick: () => addEl(p.type) })),
    };

    return (
        <div
            // The cell is a drop target for appending an element at the end (or into an empty cell);
            // per-chip drops handle precise insertion and stopPropagation so this only fires in the gap.
            onDragOver={(e) => { if (!draggingAny || !isLayoutDrag(e)) return; e.preventDefault(); if (elements.length === 0 && !cellHot) setCellHot(true); }}
            onDragLeave={() => cellHot && setCellHot(false)}
            onDrop={(e) => { if (!draggingAny) return; e.preventDefault(); performDrop(elements.length); setCellHot(false); }}
            style={{ border: `1px dashed ${cellHot ? '#789c2f' : 'transparent'}`, borderRadius: 4 }}
        >
            <Stack tokens={{ childrenGap: 4 }}>
                {elements.length === 0 && <Text variant='small' styles={{ root: { color: '#a19f9d' } }}>(empty cell){draggingElement ? ' — drop here' : ''}</Text>}

                {elements.map((el, idx) => {
                    const meta = META(el.type);
                    const configurable = !NO_CONFIG.has(el.type);
                    const calloutId = `lec-${el.id}`;
                    const indic = indicatorAt(idx);
                    return (
                        <div
                            key={el.id}
                            style={{ position: 'relative', opacity: dimmed(idx) ? 0.5 : 1 }}
                            onDragOver={(e) => { if (!draggingAny || !isLayoutDrag(e)) return; e.preventDefault(); const after = dropAfter(e, e.currentTarget, true); const cur = dnd?.over; if (!cur || cur.row !== row || cur.col !== col || cur.index !== idx || cur.after !== after) dnd?.setOverElement({ row, col, index: idx, after }); }}
                            onDrop={(e) => { if (!draggingAny) return; e.preventDefault(); e.stopPropagation(); performDrop(idx + (dropAfter(e, e.currentTarget, true) ? 1 : 0)); }}
                        >
                            {indic !== null && (
                                <div style={{ position: 'absolute', left: 0, right: 0, height: 3, borderRadius: 2, background: '#789c2f', zIndex: 2, top: indic ? undefined : -3, bottom: indic ? -3 : undefined }} />
                            )}
                            <Stack horizontal verticalAlign='center' tokens={{ childrenGap: 2 }} styles={{ root: { background: '#f3f2f1', borderRadius: 4, padding: '2px 4px' } }}>
                                {dndActive && (
                                    <span
                                        className='sf-layout-grip'
                                        draggable
                                        onDragStart={(e) => dnd?.startElementDrag(e, { row, col, el: idx })}
                                        onDragEnd={() => dnd?.endDrag()}
                                        title='Drag to move element (or use the arrows)'
                                        aria-hidden='true'
                                        style={{ display: 'inline-flex', alignItems: 'center', color: '#8a8886' }}
                                    >
                                        <Icon iconName='GripperDotsVertical' />
                                    </span>
                                )}
                                <IconButton iconProps={{ iconName: meta?.icon ?? 'CubeShape' }} styles={{ root: { height: 24, width: 24, cursor: 'default' } }} disabled title={meta?.label ?? el.type} />
                                <Text variant='small' styles={{ root: { flexGrow: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }}>{meta?.label ?? el.type}</Text>
                                {configurable && (
                                    <span id={calloutId}>
                                        <TooltipHost content='Configure'>
                                            <IconButton iconProps={{ iconName: 'Settings' }} ariaLabel='Configure element' styles={{ root: { height: 24, width: 24 } }} onClick={() => setEditing(editing === el.id ? null : el.id)} />
                                        </TooltipHost>
                                    </span>
                                )}
                                <IconButton iconProps={{ iconName: 'ChevronUpSmall' }} ariaLabel='Move element up' disabled={idx === 0} styles={{ root: { height: 24, width: 24 } }} onClick={() => moveEl(idx, -1)} />
                                <IconButton iconProps={{ iconName: 'ChevronDownSmall' }} ariaLabel='Move element down' disabled={idx === elements.length - 1} styles={{ root: { height: 24, width: 24 } }} onClick={() => moveEl(idx, 1)} />
                                <IconButton iconProps={{ iconName: 'Delete' }} ariaLabel='Delete element' styles={{ root: { height: 24, width: 24 } }} onClick={() => delEl(idx)} />

                                {editing === el.id && configurable && (
                                    <Callout target={`#${calloutId}`} onDismiss={() => setEditing(null)} directionalHint={DirectionalHint.bottomRightEdge} setInitialFocus gapSpace={4} styles={{ root: { padding: 12, maxWidth: 340 } }}>
                                        <ElementConfig element={el} onChange={(patch) => setEl(idx, patch)} />
                                    </Callout>
                                )}
                            </Stack>
                        </div>
                    );
                })}

                <Stack horizontal>
                    <DefaultButton text='Add element' iconProps={{ iconName: 'Add' }} menuProps={addMenu} styles={{ root: { height: 28, padding: '0 8px' }, label: { fontWeight: 400, fontSize: 12 } }} />
                </Stack>
            </Stack>
        </div>
    );
};

// --- Per-type config -----------------------------------------------------------------------------

const ORIENTATION_OPTIONS: IDropdownOption[] = [
    { key: 'vertical', text: 'Vertical (between columns)' },
    { key: 'horizontal', text: 'Horizontal (between rows)' },
];

const ElementConfig: React.FC<{ element: ILayoutElement; onChange: (patch: Partial<ILayoutElement>) => void }> = ({ element, onChange }) => {
    const props = (element.props ?? {}) as Record<string, unknown>;
    const setProp = (key: string, value: unknown) => onChange({ props: { ...props, [key]: value } });
    const str = (v: unknown) => (typeof v === 'string' ? v : '');

    switch (element.type) {
        case 'text':
            return (
                <DynamicSetting id={`storefront.layoutElement.${element.id}.text`}>
                    <TextField label='Text' multiline autoAdjustHeight value={str(props.text)} onChange={(_, v) => setProp('text', v ?? '')} />
                </DynamicSetting>
            );
        case 'image':
            return (
                <Stack tokens={{ childrenGap: 8 }}>
                    <DynamicSetting id={`storefront.layoutElement.${element.id}.imageUrl`}>
                        <TextField label='Image URL' value={str(props.url)} onChange={(_, v) => setProp('url', v ?? '')} />
                    </DynamicSetting>
                    <DynamicSetting id={`storefront.layoutElement.${element.id}.imageAlt`}>
                        <TextField label='Alt text' value={str(props.alt)} onChange={(_, v) => setProp('alt', v ?? '')} />
                    </DynamicSetting>
                </Stack>
            );
        case 'html':
            return (
                <Stack tokens={{ childrenGap: 4 }}>
                    <DynamicSetting id={`storefront.layoutElement.${element.id}.html`}>
                        <TextField label='Custom HTML' multiline rows={6} value={str(props.html)} onChange={(_, v) => setProp('html', v ?? '')} styles={{ field: { fontFamily: 'monospace', fontSize: 12 } }} />
                    </DynamicSetting>
                    <Text variant='small' styles={{ root: { color: '#605e5c' } }}>Scripts, event handlers, and javascript: URLs are stripped before rendering.</Text>
                </Stack>
            );
        case 'divider':
            return (
                <DynamicSetting id={`storefront.layoutElement.${element.id}.orientation`}>
                    <Dropdown label='Orientation' options={ORIENTATION_OPTIONS} selectedKey={str(props.orientation) || 'vertical'} onChange={(_, o) => o && setProp('orientation', o.key as string)} />
                </DynamicSetting>
            );
        case 'link':
            return <LinkFields link={props as IHeaderLink} onChange={(l) => onChange({ props: { ...l } as Record<string, unknown> })} settingKeyPrefix={`storefront.layoutElement.${element.id}`} />;
        case 'linkGroup':
            return <LinkGroupFields links={(props.links as IHeaderLink[] | undefined) ?? []} onChange={(links) => setProp('links', links)} settingKeyPrefix={`storefront.layoutElement.${element.id}`} />;
        case 'menu':
            return (
                <Stack tokens={{ childrenGap: 8 }}>
                    <DynamicSetting id={`storefront.layoutElement.${element.id}.menuLabel`}>
                        <TextField label='Menu label' value={str(props.label)} onChange={(_, v) => setProp('label', v ?? '')} />
                    </DynamicSetting>
                    <LinkGroupFields links={(props.links as IHeaderLink[] | undefined) ?? []} onChange={(links) => setProp('links', links)} />
                </Stack>
            );
        case 'social':
            return (
                <Stack tokens={{ childrenGap: 8 }}>
                    <Text variant='small' styles={{ root: { color: '#605e5c' } }}>Icons come from the storefront&rsquo;s social links (Facebook, X, LinkedIn, YouTube) under Footer settings.</Text>
                    <DynamicSetting id={`storefront.layoutElement.${element.id}.iconSize`}>
                        <TextField label='Icon size (px)' type='number' value={typeof props.size === 'number' ? String(props.size) : ''} placeholder='20' onChange={(_, v) => setProp('size', v ? Number(v) : undefined)} styles={{ root: { width: 120 } }} />
                    </DynamicSetting>
                </Stack>
            );
        default:
            return <Text variant='small'>No options for this element.</Text>;
    }
};

// A single IHeaderLink's editable fields (label / url / new-tab / icon). Nested submenus and mega-menu
// panels are edited in the dedicated header-links editor, not here.
const LinkFields: React.FC<{ link: IHeaderLink; onChange: (link: IHeaderLink) => void; settingKeyPrefix?: string }> = ({ link, onChange, settingKeyPrefix }) => {
    const mark = (property: string, control: React.ReactElement) =>
        settingKeyPrefix ? <DynamicSetting key={property} id={settingKeyPrefix + '.' + property}>{control}</DynamicSetting> : control;
    return (
    <Stack tokens={{ childrenGap: 8 }}>
        {mark('label', <TextField label='Label' value={link.label ?? ''} onChange={(_, v) => onChange({ ...link, label: v ?? '' })} />)}
        {mark('url', <TextField label='URL' value={link.url ?? ''} onChange={(_, v) => onChange({ ...link, url: v ?? '' })} />)}
        {mark('iconUrl', <TextField label='Icon URL (optional)' value={link.iconUrl ?? ''} onChange={(_, v) => onChange({ ...link, iconUrl: v || undefined })} />)}
        {mark('newTab', <Toggle label='Open in a new tab' checked={!!link.newTab} onChange={(_, c) => onChange({ ...link, newTab: c })} />)}
    </Stack>
    );
};

// A reorderable list of links for a linkGroup element.
const LinkGroupFields: React.FC<{ links: IHeaderLink[]; onChange: (links: IHeaderLink[]) => void; settingKeyPrefix?: string }> = ({ links, onChange, settingKeyPrefix }) => {
    const setLink = (i: number, l: IHeaderLink) => onChange(links.map((x, k) => (k === i ? l : x)));
    const add = () => onChange([...links, { id: genId('link'), label: '', url: '' }]);
    const del = (i: number) => onChange(links.filter((_, k) => k !== i));
    const move = (i: number, dir: -1 | 1) => {
        const j = i + dir;
        if (j < 0 || j >= links.length) return;
        const next = [...links];
        [next[i], next[j]] = [next[j], next[i]];
        onChange(next);
    };
    return (
        <Stack tokens={{ childrenGap: 8 }}>
            <Label styles={{ root: { padding: 0 } }}>Links</Label>
            {links.length === 0 && <Text variant='small' styles={{ root: { color: '#a19f9d' } }}>No links yet.</Text>}
            {links.map((l, i) => (
                <div key={l.id ?? i} style={{ border: '1px solid #edebe9', borderRadius: 4, padding: 6 }}>
                    <Stack horizontal horizontalAlign='space-between' verticalAlign='center'>
                        <Text variant='small' styles={{ root: { color: '#605e5c' } }}>Link {i + 1}</Text>
                        <Stack horizontal tokens={{ childrenGap: 0 }}>
                            <IconButton iconProps={{ iconName: 'ChevronUpSmall' }} ariaLabel='Move up' disabled={i === 0} styles={{ root: { height: 22, width: 22 } }} onClick={() => move(i, -1)} />
                            <IconButton iconProps={{ iconName: 'ChevronDownSmall' }} ariaLabel='Move down' disabled={i === links.length - 1} styles={{ root: { height: 22, width: 22 } }} onClick={() => move(i, 1)} />
                            <IconButton iconProps={{ iconName: 'Delete' }} ariaLabel='Delete link' styles={{ root: { height: 22, width: 22 } }} onClick={() => del(i)} />
                        </Stack>
                    </Stack>
                    {settingKeyPrefix
                        ? <DynamicSetting id={settingKeyPrefix + '.link' + (i + 1) + '.label'}><TextField placeholder='Label' value={l.label ?? ''} onChange={(_, v) => setLink(i, { ...l, label: v ?? '' })} styles={{ root: { marginTop: 4 } }} /></DynamicSetting>
                        : <TextField placeholder='Label' value={l.label ?? ''} onChange={(_, v) => setLink(i, { ...l, label: v ?? '' })} styles={{ root: { marginTop: 4 } }} />}
                    {settingKeyPrefix
                        ? <DynamicSetting id={settingKeyPrefix + '.link' + (i + 1) + '.url'}><TextField placeholder='URL' value={l.url ?? ''} onChange={(_, v) => setLink(i, { ...l, url: v ?? '' })} styles={{ root: { marginTop: 4 } }} /></DynamicSetting>
                        : <TextField placeholder='URL' value={l.url ?? ''} onChange={(_, v) => setLink(i, { ...l, url: v ?? '' })} styles={{ root: { marginTop: 4 } }} />}
                </div>
            ))}
            <Stack horizontal>
                <DefaultButton text='Add link' iconProps={{ iconName: 'Add' }} styles={{ root: { height: 28 } }} onClick={add} />
            </Stack>
        </Stack>
    );
};

export default LayoutElementCell;
